import { boundingBox, canMoveInDirection, occupiedCells } from '../model/shape';
import { occupiedKeysExcept, slideAndExitAgainst, slideFullyAgainst } from '../model/free-move';
import { poweredPlateIds } from '../model/power';
import { solveFree } from '../solver/free-solver';
const DIRS = ['up', 'down', 'left', 'right'];
/** The score a fully-maxed board (every signal at its reference maximum) lands on. */
export const SCORE_SCALE = 70;
/**
 * Node budget for the optimal-move BFS, measured against real generated boards:
 * Easy/Medium/Hard settle in 1k–95k nodes (≤ ~350ms), while a minority of 7-block
 * 8×8 Expert boards do not converge even at 1M nodes (~6s). Paying 6s per level
 * to score those exactly is not worth it, so the search is capped here and those
 * boards are scored from a flagged lower bound instead (`exact: false`).
 *
 * This is deliberately NOT the band's `solverLimit` (15k–150k): that budget is
 * tuned for the generator's accept/reject screens, and at 40k the Medium band
 * silently produced inexact scores.
 */
export const SCORE_NODE_BUDGET = 120000;
/**
 * Reference maxima — the value of each raw signal that normalises to 1.0. These
 * are framework-informed starting points chosen against the module's ceiling
 * (10×10 boards, 7 colours, 6 shape families); WP-35.10 tunes them against the
 * real level curve.
 */
export const REFERENCE_MAXIMA = {
    /** Optimal move count of a hard late-game board. */
    moves: 40,
    /** Mean legal moves per turn along the solution, above the forced-move floor of 1. */
    branchingFactor: 11,
    /** Turns along the solution where the player had more than one legal move. */
    decisionPoints: 20,
    /** Fraction of the grid covered by blocks. Boards denser than this are rare. */
    occupancy: 0.6,
    /** Distinct shape families in play, above the single-family floor of 1. */
    shapeVariety: 5,
    /** Distinct colours in play, above the single-colour floor of 1. */
    colourCount: 6,
    /** log10 of the nodes the optimal search expanded — 1e5 nodes is the reference. */
    searchDepthLog: 5,
};
const atLeastZero = (n) => (n > 0 ? n : 0);
/** Normalise the raw metrics to ~0..1 per signal. Floors at 0, no upper cap. */
export function normaliseMetrics(metrics) {
    return {
        moves: atLeastZero(metrics.moves / REFERENCE_MAXIMA.moves),
        branchingFactor: atLeastZero((metrics.branchingFactor - 1) / REFERENCE_MAXIMA.branchingFactor),
        decisionPoints: atLeastZero(metrics.decisionPoints / REFERENCE_MAXIMA.decisionPoints),
        occupancy: atLeastZero(metrics.occupancy / REFERENCE_MAXIMA.occupancy),
        shapeVariety: atLeastZero((metrics.shapeVariety - 1) / REFERENCE_MAXIMA.shapeVariety),
        colourCount: atLeastZero((metrics.colourCount - 1) / REFERENCE_MAXIMA.colourCount),
        searchDepth: atLeastZero(Math.log10(Math.max(1, metrics.searchNodes)) / REFERENCE_MAXIMA.searchDepthLog),
    };
}
/**
 * Combine normalised signals into the 0–70+ score: a weight-normalised mean
 * scaled by `SCORE_SCALE`. Dividing by the weight total keeps the range stable
 * when the Studio retunes weights — only their RATIO changes the outcome, so
 * zeroing a weight removes that signal instead of shrinking every score.
 */
export function scoreDifficulty(metrics, weights) {
    const components = normaliseMetrics(metrics);
    let weighted = 0;
    let total = 0;
    for (const key of Object.keys(components)) {
        const weight = weights[key];
        weighted += components[key] * weight;
        total += weight;
    }
    const score = total === 0 ? 0 : (SCORE_SCALE * weighted) / total;
    return { score: Math.round(score * 10) / 10, components, metrics };
}
// ─── Measurement ─────────────────────────────────────────────────────────────
/**
 * A rotation-insensitive key for a block's shape family: a vertical 1×3 and a
 * horizontal one are the same family, while an L and a 2×2 (same bounding box,
 * different cell count) are not.
 */
function shapeFamilyKey(block) {
    const bb = boundingBox(block);
    const short = Math.min(bb.width, bb.height);
    const long = Math.max(bb.width, bb.height);
    return `${short}x${long}:${occupiedCells(block).length}`;
}
/**
 * Every legal (block, direction) slide available in this position.
 *
 * Movement only, deliberately: a move counts as available when the block can take
 * at least one step that way, and whether that slide would end at a gate or at a
 * wall does not change whether the option exists. `slideFullyAgainst` and
 * `slideAndExitAgainst` answer "did it move at all" identically, so the exit rule
 * (WP-50.4) does not reach this number.
 */
function legalMoveCount(blocks, gridSize) {
    let count = 0;
    for (const block of blocks) {
        const others = occupiedKeysExcept(blocks, block.id);
        for (const dir of DIRS) {
            if (!canMoveInDirection(block, dir))
                continue;
            if (slideFullyAgainst(block, dir, others, gridSize))
                count++;
        }
    }
    return count;
}
/**
 * Replay the optimal solution and measure the choice the player actually faced
 * at each turn.
 *
 * Why replay rather than instrument the BFS: the search's branching is dominated
 * by states no solver-following player ever visits. What makes a board feel hard
 * is how many options were open ON the winning line, and that is what this
 * measures — at a cost of one board scan per move, which is negligible next to
 * the search itself.
 */
function measureSolutionPath(blocks, exits, gridSize, moves, plates) {
    let live = blocks.map((b) => ({ ...b }));
    let totalOptions = 0;
    let decisionPoints = 0;
    let turns = 0;
    for (const move of moves) {
        const options = legalMoveCount(live, gridSize);
        totalOptions += options;
        if (options > 1)
            decisionPoints++;
        turns++;
        const block = live.find((b) => b.id === move.blockId);
        if (!block)
            break;
        // Resolved through the one canonical composition (WP-50.4): the slide stops
        // at a gate the block can go through, so this walk sees the same board the
        // player would. Joining `slideFullyAgainst` to `exitingGate` by hand here is
        // what made the difficulty score describe a different game (AUD-010).
        // Module 63 — the power set is re-derived per step from the LIVE board,
        // exactly as the solver and the renderer do; a set computed once up front
        // would hold a gate open after its anchor had left.
        const outcome = slideAndExitAgainst(block, move.direction, occupiedKeysExcept(live, block.id), exits, gridSize, { poweredPlateIds: poweredPlateIds(live, plates) });
        if (!outcome)
            break;
        const slid = outcome.moved;
        live = outcome.exited
            ? live.filter((b) => b.id !== slid.id)
            : live.map((b) => (b.id === slid.id ? slid : b));
    }
    return {
        branchingFactor: turns === 0 ? 0 : totalOptions / turns,
        decisionPoints,
    };
}
/**
 * Measure a solver-verified board. Runs ONE bounded BFS for the optimal move
 * count (the reason this is affordable only on an accepted board, never on a
 * rejected candidate — see `clearableFree`).
 */
export function measureBoard(blocks, exits, gridSize, options = {}) {
    const solved = solveFree(blocks, exits, gridSize, {
        maxNodes: options.maxNodes ?? SCORE_NODE_BUDGET,
        ...(options.plates ? { plates: options.plates } : {}),
    });
    const cells = blocks.reduce((sum, b) => sum + occupiedCells(b).length, 0);
    const shapeVariety = new Set(blocks.map(shapeFamilyKey)).size;
    const colourCount = new Set(blocks.map((b) => b.colorId)).size;
    const path = solved.moves
        ? measureSolutionPath(blocks, exits, gridSize, solved.moves, options.plates)
        : // No optimal line to walk: fall back to the choice available at turn 1,
            // which is still a real (if weaker) signal of how open the board is.
            { branchingFactor: legalMoveCount(blocks, gridSize), decisionPoints: 0 };
    return {
        moves: solved.moves ? solved.moves.length : solved.depthReached,
        branchingFactor: path.branchingFactor,
        decisionPoints: path.decisionPoints,
        occupancy: gridSize === 0 ? 0 : cells / (gridSize * gridSize),
        shapeVariety,
        colourCount,
        searchNodes: solved.nodes,
        exact: solved.moves !== null,
    };
}
/**
 * Node budget for the PROXY score the generator targets against (WP-35.10).
 *
 * The exact score costs up to ~350ms per board, which cannot run several times
 * per level inside the 300ms generation budget. This budget is small enough to
 * cost single-digit milliseconds and large enough to solve most early/mid boards
 * outright; where it does not, the same lower-bound path the exact score uses
 * keeps the estimate conservative.
 *
 * The proxy is the SAME function and the SAME normalisation as the exact score —
 * only the search budget differs — so the two stay on one 0–70+ scale. It reads
 * systematically lower on boards the probe cannot solve (fewer moves, smaller
 * `searchNodes`), which is why the curve is targeted with a tolerance rather than
 * an equality.
 */
export const PROXY_NODE_BUDGET = 4000;
/**
 * Fast, in-loop score for choosing between candidate boards. Same scale and
 * meaning as `scoreBoard`, bounded by `PROXY_NODE_BUDGET`.
 */
export function proxyScoreBoard(blocks, exits, gridSize, weights, plates) {
    return scoreBoard(blocks, exits, gridSize, weights, {
        maxNodes: PROXY_NODE_BUDGET,
        ...(plates ? { plates } : {}),
    });
}
/** Measure + score in one call — what the generator and the Studio both use. */
export function scoreBoard(blocks, exits, gridSize, weights, options = {}) {
    return scoreDifficulty(measureBoard(blocks, exits, gridSize, options), weights);
}
